const React = require('react');
const PropTypes = require('prop-types');
const { filterAddonResources } = require('../../../caroline/addons/portugueseStreams');
const classnames = require('classnames');
const { deepEqual } = require('fast-equals');
const { useTranslation } = require('react-i18next');
const { useCore } = require('stremio/core');
const { Image } = require('stremio/components');
const { VERIFICATION_WINDOW_MS } = require('../../../caroline/playback/verification');
const { getManualSources, createManualSources, registerManualSources, sourceIdentity, manualSourcesGroup } = require('../../../caroline/playback/manualSources');
const styles = require('./styles');

const SourcesMenu = ({ className, type, metaId, videoId, currentPath, currentStream, onSelect, onClose, initialChoices, initialLoading }) => {
    const { t } = useTranslation();
    const core = useCore();
    const group = manualSourcesGroup(type, metaId, videoId);
    const [choices, setChoices] = React.useState(() => initialChoices || getManualSources(group));
    const [sources, setSources] = React.useState(null);
    const [expired, setExpired] = React.useState(false);
    const cached = React.useRef(choices.length > 0);

    // A refreshed/direct Player route has no Details cache. Load only the
    // existing MetaDetails stream request on explicit menu opening, never guessStream.
    React.useEffect(() => {
        if (initialChoices || cached.current) return;
        let active = true;
        let revision = 0;
        const update = async (models) => {
            if (!models.includes('meta_details')) return;
            const current = ++revision;
            try {
                const state = await core.transport.getState('meta_details');
                if (!active || current !== revision || state.selected?.metaPath?.id !== metaId ||
                state.selected?.streamPath?.id !== videoId || state.selected?.streamPath?.type !== type) return;
                const next = { loading: state.streams.some((item) => item.content.type === 'Loading'),
                    streams: filterAddonResources(state.streams).filter((item) => item.content.type === 'Ready').flatMap((item) => item.content.content)
                        .map(({ url, infoHash, fileIdx, name, title, description, behaviorHints, deepLinks }) =>
                            ({ url, infoHash, fileIdx, name, title, description, behaviorHints, deepLinks: { player: deepLinks?.player } })) };
                setSources((previous) => deepEqual(previous, next) ? previous : next);
            } catch (_) {
                if (active && current === revision) setSources({ loading: false, streams: [] });
            }
        };
        core.on('state', update);
        core.transport.dispatch({ action: 'Load', args: { model: 'MetaDetails', args: {
            metaPath: { resource: 'meta', type, id: metaId, extra: [] },
            streamPath: { resource: 'stream', type, id: videoId, extra: [] }, guessStream: false,
        } } }, 'meta_details');
        update(['meta_details']);
        const timer = setTimeout(() => setExpired(true), VERIFICATION_WINDOW_MS);
        return () => {
            active = false;
            clearTimeout(timer);
            core.off('state', update);
            core.transport.dispatch({ action: 'Unload' }, 'meta_details');
        };
    }, [core, type, metaId, videoId, initialChoices]);

    React.useEffect(() => {
        if (!sources) return;
        const results = sources.streams.map((stream) => ({ stream, state: 'unknown' }));
        registerManualSources(group, results);
        setChoices(createManualSources(results));
    }, [sources, group]);

    const displayedChoices = initialChoices || choices;
    const loading = initialChoices ? initialLoading : !expired && (!cached.current && (!sources || sources.loading) && displayedChoices.length === 0);
    const currentIdentity = sourceIdentity(currentStream);
    return (
        <div className={classnames(className, styles['sources-menu'])} role={'dialog'} aria-label={t('CAROLINE_SOURCE_OPTIONS', { defaultValue: 'Opções de reprodução' })}
            onMouseDown={(event) => { event.nativeEvent.sourcesMenuClosePrevented = true; }}>
            <header className={styles['header']}>
                <h2>{t('CAROLINE_SOURCE_OPTIONS', { defaultValue: 'Opções de reprodução' })}</h2>
                <button type={'button'} onClick={onClose} aria-label={t('CLOSE', { defaultValue: 'Fechar' })}>×</button>
            </header>
            <p className={styles['hint']}>{t('CAROLINE_SOURCE_MANUAL', { defaultValue: 'Escolhe outra opção sem sair do vídeo.' })}</p>
            <div className={styles['choices']}>
                {displayedChoices.map((choice, index) => {
                    const selected = currentIdentity ? choice.identity === currentIdentity : choice.launchLink?.replace(/^#/, '').split('?')[0] === currentPath.split('?')[0];
                    return (
                        <button type={'button'} className={classnames(styles['choice'], { [styles['selected']]: selected })} key={choice.identity || choice.launchLink}
                            aria-current={selected ? 'true' : undefined} disabled={selected} onClick={() => onSelect(choice.path, choice.identity, choice.launchLink)}>
                            <span className={styles['name']}>{t('CAROLINE_SOURCE_NUMBER', { defaultValue: 'Opção {{number}}', number: index + 1 })}
                                {selected ? <span className={styles['current']}>{t('CAROLINE_CURRENT_SOURCE', { defaultValue: 'Atual' })}</span> : null}</span>
                            <span className={styles['details']}>{[choice.quality, choice.language].filter(Boolean).join(' · ')}</span>
                        </button>
                    );
                })}
                {loading ? <div className={styles['loading']} role={'status'}>
                    <Image src={require('/assets/images/caroline-owl.png')} alt={''} />
                    <span>{t('CAROLINE_STREAM_SEARCHING', { defaultValue: 'A procurar opções…' })}</span>
                </div> : displayedChoices.length === 0 ? <p className={styles['hint']}>{t('CAROLINE_SOURCE_NOT_VERIFIED', { defaultValue: 'Não foi possível confirmar outras opções neste momento.' })}</p> : null}
            </div>
        </div>
    );
};

SourcesMenu.propTypes = { className: PropTypes.string, type: PropTypes.string.isRequired, metaId: PropTypes.string.isRequired,
    videoId: PropTypes.string.isRequired, currentPath: PropTypes.string.isRequired, currentStream: PropTypes.object,
    onSelect: PropTypes.func.isRequired, onClose: PropTypes.func.isRequired, initialChoices: PropTypes.array, initialLoading: PropTypes.bool };
module.exports = SourcesMenu;
